import { createTheme, alpha } from "@mui/material/styles";

export const AppTheme = (mode) => {
  const isDark = mode === "dark";

  const brand = {
    main: "#10a37f",
    light: "#3dbfa0",
    dark: "#0b7a5e",
    contrast: "#ffffff",
  };

  const palette = {
    mode,
    primary: brand,
    secondary: { main: isDark ? "#6c63ff" : "#5b52e0" },
    background: {
      default: isDark ? "#0f1117" : "#f0f2f5",
      paper: isDark ? "#1a1d27" : "#ffffff",
      elevated: isDark ? "#222636" : "#f8f9fc",
    },
    text: {
      primary: isDark ? "#e8eaed" : "#1a1a2e",
      secondary: isDark ? "#8b92a5" : "#5f6b7a",
    },
    divider: isDark ? alpha("#ffffff", 0.08) : alpha("#000000", 0.08),
    error: { main: "#ef4444" },
    warning: { main: "#f59e0b" },
    success: { main: "#22c55e" },
    info: { main: "#3b82f6" },
    // Custom tokens
    glass: {
      bg: isDark ? alpha("#1a1d27", 0.7) : alpha("#ffffff", 0.7),
      border: isDark ? alpha("#ffffff", 0.08) : alpha("#000000", 0.06),
      shadow: isDark
        ? "0 8px 32px rgba(0,0,0,0.4)"
        : "0 8px 32px rgba(0,0,0,0.08)",
    },
    gradient: {
      bg: isDark
        ? "linear-gradient(135deg, #0f1117 0%, #1a1d27 50%, #16192a 100%)"
        : "linear-gradient(135deg, #f0f2f5 0%, #e8ecf4 50%, #f0f2f5 100%)",
      brand:
        "linear-gradient(135deg, #10a37f 0%, #0b7a5e 50%, #086b52 100%)",
      card: isDark
        ? `linear-gradient(145deg, ${alpha("#1a1d27", 0.9)}, ${alpha("#222636", 0.6)})`
        : `linear-gradient(145deg, ${alpha("#ffffff", 0.9)}, ${alpha("#f8f9fc", 0.6)})`,
    },
  };

  return createTheme({
    palette,
    typography: {
      fontFamily: "'Inter', 'Segoe UI', 'Roboto', sans-serif",
      h1: { fontWeight: 800, letterSpacing: "-0.02em" },
      h2: { fontWeight: 700, letterSpacing: "-0.01em" },
      h3: { fontWeight: 700 },
      h4: { fontWeight: 600 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      subtitle1: { fontWeight: 500 },
      body1: { lineHeight: 1.6 },
      body2: { lineHeight: 1.5 },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: { scrollBehavior: "smooth" },
          body: {
            overflowX: "hidden",
            transition: "background-color 0.3s ease, color 0.3s ease",
          },
          "*::-webkit-scrollbar": { width: 5 },
          "*::-webkit-scrollbar-track": { background: "transparent" },
          "*::-webkit-scrollbar-thumb": {
            background: isDark ? alpha("#fff", 0.15) : alpha("#000", 0.15),
            borderRadius: 10,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            transition: "background-color 0.3s ease, box-shadow 0.3s ease",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: 10,
            fontWeight: 600,
            padding: "8px 20px",
            transition: "all 0.2s ease",
          },
          containedPrimary: {
            background: palette.gradient.brand,
            boxShadow: `0 4px 14px ${alpha(brand.main, 0.4)}`,
            "&:hover": {
              boxShadow: `0 6px 20px ${alpha(brand.main, 0.5)}`,
              transform: "translateY(-1px)",
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: { variant: "outlined", size: "small" },
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 10,
              transition: "box-shadow 0.2s ease",
              "&.Mui-focused": {
                boxShadow: `0 0 0 3px ${alpha(brand.main, 0.15)}`,
              },
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            backgroundImage: "none",
            border: `1px solid ${palette.glass.border}`,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 8,
            fontSize: "0.75rem",
            backgroundColor: isDark ? "#333" : "#555",
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, borderRadius: 8 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            minHeight: 40,
          },
        },
      },
    },
  });
};
